/* Design tokens, ported verbatim from prototype/contract-ops.jsx.
   These values are a specification, not a starting point. Every screen in
   every phase reads from here so the federal identity cannot drift. */

export const T = {
  bg: "#F4EFE3",
  panel: "#FDFBF4",
  ink: "#1A2333",
  navy: "#14284B",
  steel: "#2E4A7A",
  gold: "#B08C3C",
  ok: "#3E6B4F",
  warn: "#9A6A1F",
  risk: "#8F2B25",
  line: "#D9D2BE",
  soft: "#5F6B7C",
  chip: "#EDE7D6",
  sideText: "#C9D2E0",
};

/* Status colour.
 *
 * Four states, and the same four everywhere: blue for work that has not
 * started, amber for work underway, green for work finished, red for anything
 * overdue or in trouble. A chip on a register and a band on a chart therefore
 * mean the same thing, which is the whole point of fixing them here.
 *
 * Each state carries two steps, because a filled shape and a piece of text
 * need different things from one colour.
 *
 * The fill step paints chart bands. All four were run through the palette
 * validator against the cream surface: lightness, chroma, contrast and
 * colourblind separation on every pair. The worst pair is amber against green
 * at 8.2, which clears the floor of 8, and the old green failed that same pair
 * at 6.0, which is why green here is a step lighter than the brand green.
 *
 * The ink step is darker and is what a chip's text is set in. The fill steps
 * land between 3.0 and 3.6 to 1 against the chip's tinted background, and small
 * bold capitals need 4.5. Every ink step clears it. Red needs no second step;
 * the brand red is dark enough to do both jobs.
 */
export const STATUS = {
  notStarted: { fill: "#3E7DD1", ink: "#2B62AE" },
  inProgress: { fill: "#C0761A", ink: "#9A5A0C" },
  complete:   { fill: "#3E9B62", ink: "#3E6B4F" },
  overdue:    { fill: "#8F2B25", ink: "#8F2B25" },
};

export const SERIES = {
  notStarted: STATUS.notStarted.fill,
  inProgress: STATUS.inProgress.fill,
  complete:   STATUS.complete.fill,
  overdue:    STATUS.overdue.fill,
};

/* The brand gold is not in this set on purpose. Against the green it separates
   by 4.3 for a protan reader and by 13.7 for everyone else, meaning a reader
   with ordinary colour vision cannot reliably tell a gold band from a green
   one. It keeps its jobs as a panel accent and as the milestone diamond on the
   Gantt, where it is a shape rather than a category, and it is never a series
   fill beside these four. */

/* Invoice aging is not a set of categories, it is one thing getting worse, so
   it takes an ordered ramp rather than four unrelated hues. The prototype's
   colours failed on this: 61-90 and 90+ were the same red, which is to say the
   two worst buckets were drawn identically.

   These four are a single hue, brick at 30 to 37 degrees, with lightness
   stepping down 0.74, 0.63, 0.52, 0.41. Validated as an ordinal ramp: monotone
   lightness, every adjacent gap above the 0.06 floor, and the light end still
   clearing the cream surface at 2.32:1. Because they differ by lightness they
   stay in order for a reader with any kind of colour vision, and in greyscale.

   "Within terms" sits outside the ramp on purpose. It is not a mild version of
   past terms, it is the absence of the problem, so it takes the blue. */
export const AGING_RAMP = {
  "Within terms": STATUS.notStarted.fill,
  "1-30 past":    "#E29385",
  "31-60 past":   "#C66D53",
  "61-90 past":   "#A64735",
  "90+ past":     "#82270B",
};

/* Order matters: this is the fixed assignment order for categorical series,
   chosen so no two adjacent entries fail separation. */
export const SERIES_ORDER = [SERIES.notStarted, SERIES.inProgress, SERIES.complete, SERIES.overdue];

export const CHART_COLORS = [
  "#14284B", "#B08C3C", "#2E4A7A", "#8F2B25",
  "#3E6B4F", "#8C7146", "#5C6B85", "#7A5C2E",
];

export const FAMILY = "'Aptos', 'Public Sans', 'Segoe UI', sans-serif";
export const SERIF = "'Source Serif 4', Georgia, 'Times New Roman', serif";

export const FONT = `@import url('https://fonts.googleapis.com/css2?family=Public+Sans:wght@400;500;600;700&family=Source+Serif+4:wght@600;700&display=swap');
* { box-sizing: border-box; } body { margin: 0; }
input, select, textarea, button { font-family: ${FAMILY}; }
::placeholder { color: #8B93A1; }`;

/* Status colour lookup.
 *
 * Every status word in the app resolves to one of the four states. Statuses
 * arrive from spreadsheets spelled and capitalised half a dozen ways, so the
 * lookup is case-folded rather than listing each spelling, and anything the
 * app has never seen falls back to plain grey instead of being coloured wrong.
 *
 * The grouping is the argument, so it is worth stating. Blue is work nobody
 * has begun, which includes a risk or an action that is merely open. Amber is
 * work in hand: in progress, in review, being mitigated, submitted and waiting
 * on somebody. Green is finished, whatever the register calls finished. Red is
 * trouble: overdue, blocked, critical, out of compliance.
 */
const STATUS_GROUPS = {
  notStarted: ["not started", "not begun", "new", "open", "planned", "to do", "backlog"],
  inProgress: ["in progress", "in review", "under review", "mitigating", "pending",
               "submitted", "responded", "ordered", "at risk", "in work", "started",
               "ongoing", "monitoring"],
  complete:   ["complete", "completed", "done", "accepted", "closed", "compliant",
               "on track", "paid", "delivered", "approved", "resolved", "cancelled"],
  overdue:    ["overdue", "blocked", "critical", "non-compliant", "late", "escalated",
               "past due", "breached"],
};

/* Words that mean "no state recorded" and should not be coloured as if they
   were one. */
const MUTED = new Set(["n/a", "na", "none", "status not reported", "not reported",
                       "tbd", "unknown"]);

const STATUS_LOOKUP = new Map();
for (const [state, words] of Object.entries(STATUS_GROUPS)) {
  for (const w of words) STATUS_LOOKUP.set(w, STATUS[state]);
}

/* Which of the four states a word means, or null where the app has never seen
   the word and nothing should be inferred from it.
 *
 * The grouping above is a vocabulary, not only a palette: whether a permit is
 * finished is the same question as which colour it draws in. Registers arrive
 * from workbooks saying Closed, Completed and Approved for the same thing, so
 * anything deciding "is this done" must ask here rather than pick one spelling
 * and quietly report the rest as outstanding. */
export const statusState = (value) => {
  const k = String(value ?? "").trim().toLowerCase();
  if (!k || MUTED.has(k)) return null;
  for (const [state, words] of Object.entries(STATUS_GROUPS)) {
    if (words.includes(k)) return state;
  }
  return null;
};

/* The ink step, for a chip or a piece of text. */
export const statusColor = (value) => {
  const k = String(value ?? "").trim().toLowerCase();
  if (MUTED.has(k)) return T.soft;
  return STATUS_LOOKUP.get(k)?.ink ?? T.soft;
};

/* The fill step, for a chart band or a filled shape. */
export const statusFill = (value) => {
  const k = String(value ?? "").trim().toLowerCase();
  if (MUTED.has(k)) return T.soft;
  return STATUS_LOOKUP.get(k)?.fill ?? null;
};

/* Kept as a map for the few places that want to read one directly. */
export const STATUS_COLORS = Object.fromEntries(
  [...STATUS_LOOKUP].map(([word, state]) => [word, state.ink]),
);

/* The brand gold, dark enough to read as text.
 *
 * #B08C3C is a border and a diamond and a plate on a navy sidebar, and it is
 * excellent at all three. As small bold capitals on a cream panel it sits at
 * 2.75:1, which is below the 4.5 that small text needs and below even the 3
 * that large text needs, so every gold chip and gold eyebrow in the app was
 * hard to read. This is the same hue taken down until it clears 4.9 on a chip
 * and 5.6 on a panel. Gold on the navy sidebar keeps the original, where it
 * has all the contrast it needs. */
export const GOLD_INK = "#806113";

export const priorityColor = (p) =>
  p === "High" ? T.risk : p === "Medium" ? T.gold : T.steel;
